import { useParams } from 'react-router-dom'
import { Layout } from '@/shell/Layout'
import { EmptyState, PageLoading } from '@/shared/components/EmptyState'
import { useOrgs, useAsync } from '@/lib/hooks'
import { api } from '@/lib/api'
import { ProvidersList } from '@/features/providers/ProvidersList'
import { type OrgLimits } from '@/features/providers/types'

export function Providers() {
  const { orgSlug, upstream, namespace, type } = useParams<{ orgSlug: string; upstream?: string; namespace?: string; type?: string }>()
  const orgs = useOrgs()
  const selectedOrg = orgs.data?.orgs.find((o) => (o.slug ?? o.id) === orgSlug) ?? null

  const billing = useAsync<OrgLimits | null>(
    () => selectedOrg ? api.get(`/api/orgs/${selectedOrg.slug}/billing/limits`) : Promise.resolve(null),
    [selectedOrg?.slug],
  )

  const canAdmin = ['owner', 'admin'].includes(selectedOrg?.role ?? '')

  return (
    <Layout title={<>Providers <span className="badge badge-amber">Experimental</span></>}>
      {orgs.loading ? (
        // The org list resolves selectedOrg; without this guard a fresh mount flashed
        // "No organisation selected" while the fetch was still in flight.
        <PageLoading />
      ) : !selectedOrg ? (
        <EmptyState icon="⬡" title="No organisation selected" />
      ) : billing.loading ? (
        <PageLoading />
      ) : billing.error ? (
        <EmptyState icon="⚠" title="Could not load plan information" subtitle="Refresh to try again." />
      ) : !billing.data ? (
        // billing.data settles a commit after selectedOrg (the fetch short-circuits to null while
        // the org list is still resolving) — render the loader for that frame rather than deref null.
        <PageLoading />
      ) : (
        <ProvidersList
          key={selectedOrg.slug}
          org={selectedOrg}
          orgId={selectedOrg.slug}
          selUpstream={upstream}
          selNamespace={namespace}
          selType={type}
          limits={billing.data.limits}
          canAdmin={canAdmin}
        />
      )}
    </Layout>
  )
}
